// src/pages/DashboardPage.tsx
// Today's habit log — the main daily view

import React, { useState, useEffect, useCallback } from 'react';
import { logsApi, habitsApi } from '../api';
import { useAuth } from '../context/AuthContext';
import type { DayEntry, LogState, HabitGroups } from '../types';

// Weekly context computed server-side — see GET /logs response weeklyContext field

// ── DATE HELPERS ──────────────────────────────────────────────────

function toJakartaDateStr(offsetDays = 0): string {
  const now = new Date();
  // UTC+7
  const jakarta = new Date(now.getTime() + 7 * 60 * 60 * 1000 + offsetDays * 86400000);
  return jakarta.toISOString().split('T')[0];
}

function formatDisplayDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
}

// ── LOG MODAL ─────────────────────────────────────────────────────

interface LogModalProps {
  entry: DayEntry;
  date: string;
  onClose: () => void;
  onLogged: () => void;
}

function LogModal({ entry, date, onClose, onLogged }: LogModalProps) {
  const [state, setState]         = useState<LogState | null>(null);
  const [naReason, setNaReason]   = useState('');
  const [slipCount, setSlipCount] = useState(1);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  // Weekly context comes from the API — no frontend computation needed
  const wc        = entry.weeklyContext;
  const isWeekly  = wc !== null;
  const isLimit   = entry.habit.frequency === 'weekly_limit';
  const existing  = entry.log;

  const submit = async (s: LogState) => {
    if (s === 'na' && !naReason.trim()) { setState('na'); return; }
    setLoading(true); setError('');
    try {
      const payload: any = { habitId: entry.habit.habitId, date, state: s };
      if (s === 'na') payload.naReason = naReason;
      if (entry.habit.type === 'break' && s === 'missed') payload.slipCount = slipCount;

      if (existing) {
        await logsApi.update(existing.logId, payload);
      } else {
        await logsApi.create(payload);
      }
      onLogged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
      backdropFilter:'blur(4px)', zIndex:100,
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'var(--bg2)', border:'1px solid var(--border2)',
        borderRadius:'var(--radius-lg)', padding:28, width:360,
        animation:'fadeUp 0.2s ease',
      }}>
        <div style={{ marginBottom:4 }}>
          <span style={{
            fontSize:9, fontFamily:'var(--font-mono)', color:'var(--text3)',
            textTransform:'uppercase', letterSpacing:'1px',
          }}>
            {entry.habit.type === 'break' ? '🚫 Break habit' : '🎯 Build habit'} · {entry.habit.frequency.replace('_',' ')}
          </span>
        </div>
        <div style={{ fontSize:18, fontWeight:600, color:'var(--text)', marginBottom:4 }}>
          {entry.habit.name}
        </div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom: isWeekly ? 10 : 22 }}>{date}</div>

        {/* Weekly context */}
        {isWeekly && wc && (
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 14,
            background: isLimit
              ? (wc.doneThisWeek >= wc.target ? 'var(--red-dim)' : 'var(--orange-dim)')
              : (wc.doneThisWeek >= wc.target ? 'var(--green-dim)' : 'var(--bg3)'),
            border: isLimit
              ? (wc.doneThisWeek >= wc.target ? '1px solid rgba(255,94,125,0.2)' : '1px solid rgba(255,181,71,0.2)')
              : (wc.doneThisWeek >= wc.target ? '1px solid rgba(0,229,160,0.2)' : '1px solid var(--border)'),
          }}>
            {isLimit ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)',
                  color: wc.doneThisWeek >= wc.target ? 'var(--red)' : 'var(--orange)' }}>
                  {wc.doneThisWeek} of {wc.target} {wc.target === 1 ? 'time' : 'times'} used this week
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                  {wc.doneThisWeek >= wc.target
                    ? `⚠ You've reached your weekly limit. Logging another counts against you.`
                    : `${wc.remaining} remaining — log an occurrence to track usage`}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)',
                  color: wc.doneThisWeek >= wc.target ? 'var(--green)' : 'var(--text2)' }}>
                  {wc.doneThisWeek} of {wc.target} sessions done this week
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                  {wc.doneThisWeek >= wc.target
                    ? `✓ Target hit! Any extra sessions count as bonus.`
                    : `${wc.remaining} more to hit target — log this session as done`}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ padding:'8px 12px', borderRadius:'var(--radius-sm)',
            background:'var(--red-dim)', border:'1px solid rgba(255,94,125,0.2)',
            color:'var(--red)', fontSize:11, marginBottom:14 }}>
            {error}
          </div>
        )}

        {/* N/A reason input */}
        {state === 'na' && (
          <div style={{ marginBottom:14 }}>
            <input
              autoFocus
              placeholder="Why N/A today? (required)"
              value={naReason}
              onChange={e => setNaReason(e.target.value)}
              style={{
                width:'100%', padding:'10px 12px',
                background:'var(--bg3)', border:'1px solid var(--border2)',
                borderRadius:'var(--radius-sm)', color:'var(--text)', fontSize:12,
                outline:'none',
              }}
            />
            <button onClick={() => submit('na')} disabled={!naReason.trim() || loading}
              style={{
                width:'100%', marginTop:8, padding:'10px',
                background:'rgba(90,98,120,0.3)', border:'1px solid var(--border2)',
                borderRadius:'var(--radius-sm)', color:'var(--text2)', fontSize:12,
                cursor: naReason.trim() ? 'pointer' : 'not-allowed',
              }}>
              Confirm N/A
            </button>
          </div>
        )}

        {/* Break habit slip count */}
        {entry.habit.type === 'break' && state !== 'na' && (
          <div style={{ marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:11, color:'var(--text3)' }}>Slip count (if missed):</span>
            <div style={{ display:'flex', gap:6 }}>
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setSlipCount(n)}
                  style={{
                    width:28, height:28, borderRadius:'50%', fontSize:11,
                    background: slipCount === n ? 'var(--red-dim)' : 'var(--bg3)',
                    border: slipCount === n ? '1px solid var(--red)' : '1px solid var(--border)',
                    color: slipCount === n ? 'var(--red)' : 'var(--text3)',
                    cursor:'pointer',
                  }}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {state !== 'na' && (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <ActionBtn
              onClick={() => submit('done')}
              color="green"
              disabled={loading}
              icon="✓"
              label={
              entry.habit.type === 'break'
                ? 'Kept — no slip today'
                : isLimit
                ? 'Log occurrence (counts against limit)'
                : wc
                ? 'Log session as done'
                : 'Done — nailed it'
            }
            />
            <ActionBtn
              onClick={() => entry.habit.type === 'break' ? submit('missed') : submit('missed')}
              color="red"
              disabled={loading}
              icon="✗"
              label={entry.habit.type === 'break' ? `Slipped ×${slipCount}` : 'Missed today'}
            />
            <ActionBtn
              onClick={() => setState('na')}
              color="gray"
              disabled={loading}
              icon="—"
              label="Not applicable today"
            />
          </div>
        )}

        <button onClick={onClose} style={{
          width:'100%', marginTop:12, padding:'8px',
          background:'transparent', border:'none',
          color:'var(--text3)', fontSize:11, cursor:'pointer',
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, color, disabled, icon, label }: {
  onClick: () => void; color: 'green'|'red'|'gray';
  disabled: boolean; icon: string; label: string;
}) {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    green: { bg:'var(--green-dim)', border:'rgba(0,229,160,0.3)',  text:'var(--green)' },
    red:   { bg:'var(--red-dim)',   border:'rgba(255,94,125,0.3)', text:'var(--red)' },
    gray:  { bg:'var(--bg3)',       border:'var(--border2)',        text:'var(--text2)' },
  };
  const c = colors[color];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'14px 16px', borderRadius:'var(--radius-md)', fontSize:14, minHeight:52,
      fontFamily:'var(--font-ui)', cursor: disabled ? 'not-allowed' : 'pointer',
      display:'flex', alignItems:'center', gap:10,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      fontWeight:500, transition:'all 0.15s',
    }}>
      <span style={{ fontSize:18 }}>{icon}</span>
      {label}
    </button>
  );
}

// ── HABIT CARD ────────────────────────────────────────────────────

function HabitCard({ entry, onTap }: { entry: DayEntry; onTap: () => void }) {
  const { habit, log } = entry;
  const state = log?.state;

  const stateStyle = state === 'done'
    ? { bg:'var(--green-dim)', border:'rgba(0,229,160,0.2)', bar:'var(--green)', name:'var(--green)' }
    : state === 'missed'
    ? { bg:'var(--red-dim)', border:'rgba(255,94,125,0.2)', bar:'var(--red)', name:'var(--red)' }
    : state === 'na'
    ? { bg:'transparent', border:'var(--border)', bar:'var(--text3)', name:'var(--text3)' }
    : { bg:'var(--bg2)', border:'var(--border2)', bar:'var(--border2)', name:'var(--text)' };

  return (
    <div onClick={onTap} style={{
      background: stateStyle.bg,
      border: `1px solid ${stateStyle.border}`,
      borderRadius: 'var(--radius-md)',
      padding: '16px',
      display: 'flex', alignItems: 'center', gap: 14,
      cursor: 'pointer', transition: 'all 0.2s',
      position: 'relative', overflow: 'hidden',
    }}
      onMouseEnter={e => (e.currentTarget.style.transform = 'translateX(3px)')}
      onMouseLeave={e => (e.currentTarget.style.transform = 'translateX(0)')}>

      {/* Left color bar */}
      <div style={{
        position:'absolute', left:0, top:0, bottom:0,
        width:3, borderRadius:'3px 0 0 3px',
        background: stateStyle.bar,
      }} />

      {/* Check circle */}
      <div style={{
        width:36, height:36, borderRadius:'50%', flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        border: state ? 'none' : '2px solid var(--border2)',
        background: state === 'done' ? 'var(--green)' : state === 'missed' ? 'var(--red)' : 'transparent',
        color: state ? '#0c0e14' : 'transparent', fontSize:14, fontWeight:700,
      }}>
        {state === 'done' ? '✓' : state === 'missed' ? '✗' : state === 'na' ? '—' : ''}
      </div>

      {/* Info */}
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {habit.isIntention && <span title="Monthly intention" style={{ fontSize:10 }}>⭐</span>}
          {habit.isPriority  && !habit.isIntention && <span title="Priority" style={{ fontSize:10 }}>🎯</span>}
          <span style={{ fontSize:14, fontWeight:500, color: stateStyle.name }}>
            {habit.name}
          </span>
        </div>
        <div style={{ fontSize:10, color:'var(--text3)', marginTop:2, fontFamily:'var(--font-mono)' }}>
          {habit.type === 'break' ? '🚫 break' : '🔨 build'} · {habit.frequency.replace('_',' ')}
          {log?.naReason && <span style={{ marginLeft:6 }}>· N/A: {log.naReason}</span>}
          {log?.penaltyTotal ? <span style={{ color:'var(--red)', marginLeft:6 }}>· IDR {log.penaltyTotal.toLocaleString()} penalty</span> : null}
        </div>
      </div>

      {/* Tap hint */}
      {!state && (
        <div style={{ fontSize:9, color:'var(--text3)', fontFamily:'var(--font-mono)' }}>
          tap to log
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────

export default function DashboardPage() {
  const { user } = useAuth();
  const [date, setDate]     = useState(toJakartaDateStr());
  const [entries, setEntries] = useState<DayEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeEntry, setActiveEntry] = useState<DayEntry | null>(null);

  const today     = toJakartaDateStr();
  const yesterday = toJakartaDateStr(-1);
  const isToday   = date === today;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { entries: e } = await logsApi.getByDate(date);
      setEntries(e);
    } catch {}
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Group entries
  const priority  = entries.filter(e => e.habit.isPriority);
  const active    = entries.filter(e => !e.habit.isPriority && e.habit.status === 'active');
  const graduated = entries.filter(e => e.habit.status === 'graduated');

  const doneCount = entries.filter(e => e.log?.state === 'done').length;
  const totalCount = entries.filter(e => e.habit.status !== 'graduated').length;

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:26, fontWeight:600, letterSpacing:-0.5, color:'var(--text)' }}>
          {greet()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
        </div>
        <div style={{ fontSize:12, color:'var(--text3)', marginTop:5, fontFamily:'var(--font-mono)' }}>
          {formatDisplayDate(date)}
        </div>

        {/* Date nav */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14 }}>
          <button onClick={() => setDate(yesterday)} disabled={date === yesterday}
            style={{
              padding:'6px 12px', borderRadius:'var(--radius-sm)', fontSize:11,
              background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)',
              cursor: date === yesterday ? 'not-allowed' : 'pointer', opacity: date === yesterday ? 0.4 : 1,
            }}>
            ← Yesterday
          </button>
          <div style={{
            padding:'6px 14px', borderRadius:'var(--radius-sm)', fontSize:13,
            background:'var(--green-dim)', border:'1px solid rgba(0,229,160,0.2)',
            color:'var(--green)', fontWeight:600, fontFamily:'var(--font-mono)',
          }}>
            {isToday ? 'Today' : 'Yesterday'} · {doneCount}/{totalCount} done
          </div>
          <button onClick={() => setDate(today)} disabled={isToday}
            style={{
              padding:'6px 12px', borderRadius:'var(--radius-sm)', fontSize:11,
              background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)',
              cursor: isToday ? 'not-allowed' : 'pointer', opacity: isToday ? 0.3 : 1,
            }}>
            Today →
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color:'var(--text3)', fontSize:12, fontFamily:'var(--font-mono)' }}>loading habits...</div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:24, marginBottom:12 }}>🎯</div>
          <div style={{ fontSize:14, color:'var(--text2)' }}>No active habits yet.</div>
          <div style={{ fontSize:11, color:'var(--text3)', marginTop:6 }}>
            Go to Habits → Config to create and activate your habits.
          </div>
        </div>
      ) : (
        <>
          {priority.length > 0 && (
            <Section label={`⭐ Priority habits · ${priority.filter(e=>e.log?.state==='done').length}/${priority.length} done`}>
              {priority.map(e => (
                <HabitCard key={e.habit.habitId} entry={e} onTap={() => setActiveEntry(e)} />
              ))}
            </Section>
          )}

          {active.length > 0 && (
            <Section label="Active habits">
              {active.map(e => (
                <HabitCard key={e.habit.habitId} entry={e} onTap={() => setActiveEntry(e)} />
              ))}
            </Section>
          )}

          {graduated.length > 0 && (
            <Section label="🎓 Graduated (passive tracking)">
              {graduated.map(e => (
                <HabitCard key={e.habit.habitId} entry={e} onTap={() => setActiveEntry(e)} />
              ))}
            </Section>
          )}
        </>
      )}

      {/* Log modal */}
      {activeEntry && (
        <LogModal
          entry={activeEntry}
          date={date}
          onClose={() => setActiveEntry(null)}
          onLogged={load}
        />
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom:28 }}>
      <div style={{
        fontSize:9, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase',
        color:'var(--text3)', fontFamily:'var(--font-mono)', marginBottom:10,
      }}>
        {label}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {children}
      </div>
    </div>
  );
}
